import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import FadeInSection from "@/components/FadeInSection";
import InlineCTA from "@/components/InlineCTA";
import LazySection from "@/components/LazySection";
import NewsletterBanner from "@/components/NewsletterBanner";
import { comparisonPages } from "@/data/platformPages";
import { useSanityComparisonPages } from "@/hooks/useSanityContent";

const ComparisonPage = () => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = paramSlug || window.location.pathname.replace(/^\/|\/$/g, "");
  const { data: sanityPages } = useSanityComparisonPages();

  const sanityPage = sanityPages?.find((p) => p.slug?.current === slug);
  const hardcodedPage = sanityPages === undefined ? comparisonPages.find((p) => p.slug === slug) : null;

  const page = sanityPage
    ? {
        title: sanityPage.title,
        description: sanityPage.description ?? "",
        platformA: sanityPage.platformA ?? "",
        platformB: sanityPage.platformB ?? "",
        sections: sanityPage.sections ?? [],
        slug,
      }
    : hardcodedPage;

  if (!page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Page not found</h1>
          <Link to="/prediction-market-apps" className="text-primary hover:underline">Back to Apps & Platforms</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={page.title} description={page.description} canonical={`/${page.slug}`} breadcrumbs={[{ name: "Home", href: "/" }, { name: "Apps & Platforms", href: "/prediction-market-apps" }, { name: page.title, href: `/${page.slug}` }]} />
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-8">
          <InlineCTA variant="newsletter" />
        </div>

        <FadeInSection>
          <Link to="/prediction-market-apps" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Apps & Platforms
          </Link>
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">{page.title}</h1>
          <p className="text-muted-foreground text-lg max-w-3xl mb-10">{page.description}</p>
        </FadeInSection>

        {page.sections.map((section, i) => (
          <FadeInSection key={i} delay={0.05 + i * 0.03}>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-base text-muted-foreground leading-relaxed">{section.content}</p>
            </div>
          </FadeInSection>
        ))}

        {/* Links to individual platform pages */}
        {page.platformA && page.platformB && (
          <FadeInSection delay={0.2}>
            <div className="mt-10 pt-8 border-t border-border flex flex-wrap gap-4">
              <Link to={`/${comparisonPages.find(c => c.slug === slug)?.platformA === "Kalshi" ? "kalshi" : "polymarket-review"}/`} className="text-primary font-semibold text-sm hover:underline underline-offset-4">
                Read our {page.platformA} review
              </Link>
              <Link to={`/${page.platformB === "PredictIt" ? "predictit" : page.platformB === "Polymarket" ? "polymarket-review" : "kalshi"}/`} className="text-primary font-semibold text-sm hover:underline underline-offset-4">
                Read our {page.platformB} review
              </Link>
            </div>
          </FadeInSection>
        )}

        <div className="mt-10">
          <LazySection><NewsletterBanner /></LazySection>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;
